import type { Type } from '@angular/core';

import type { GridStackNode, GridStackWidget } from 'gridstack/dist/types';
import type { Observable } from 'rxjs';

import type { IconName } from '@core/user-interface-legacy';

export interface GridItemConfig {
  rows: number;
  cols: number;
  minItemRows: number;
  minItemCols: number;
  maxItemRows?: number;
  maxItemCols?: number;
  locked?: GridStackWidget['locked'];
  noMove?: GridStackWidget['noMove'];
  noResize?: GridStackWidget['noResize'];
}

export enum WidgetType {
  /**
   * Default widgets, position and size saved on backend
   */
  Drawer = 'drawer_widget',
  /**
   * Widget visible only in drawer with "coming soon" annotation, can't be enabled
   */
  ComingSoonDrawer = 'coming_soon',
  /**
   * Widgets that are on dashboard but backend doesn't know about their existence
   * used for promotional banners
   */
  FeOnly = 'fe_only',
}

interface WidgetDefBase<Type extends WidgetType> {
  id: string;
  type: Type;
  /**
   * @desc Runs in injection context, defaults to true
   * If false not visible in drawer, not visible on board
   */
  isAvailable?: () => Observable<boolean>;
}

export interface DrawerWidgetDef<Type extends WidgetType> extends WidgetDefBase<Type> {
  drawer: {
    name: string;
    description: string;
    icon: IconName;
    iconBgColor: 'backgroundGradientBrand' | 'backgroundGradientScale' | 'backgroundGradientLead';
  };
}

export interface WidgetGridOptions {
  config: GridItemConfig;
  onInit?: (node: GridStackNode) => void;
  component: Observable<Type<any>>;
}

export type ComingSoonWidgetDef = DrawerWidgetDef<WidgetType.ComingSoonDrawer>;
export interface WidgetDef extends DrawerWidgetDef<WidgetType.Drawer> {
  grid: WidgetGridOptions;
}
export interface FeOnlyWidgetDef extends WidgetDefBase<WidgetType.FeOnly> {
  grid: WidgetGridOptions;
}

export type AnyWidgetDef = WidgetDef | ComingSoonWidgetDef | FeOnlyWidgetDef;

export enum DashboardWidgetId {
  Recents = 'recents',
  UpcomingEvents = 'upcoming_events',
  NewCandidates = 'new_candidates',
  OverdueCandidates = 'overdue_candidates',
  AppliedCandidates = 'applied_candidates',
  JobOverview = 'job_overview',
  Evaluations = 'evaluations',
  Tasks = 'tasks',
  TagsSources = 'tags_and_sources',
  Discover = 'discover',
  AssistantSuggestion = 'assistant_suggestion',
}

export type WidgetId = string;

export interface WidgetState {
  id: WidgetId;
  grid: GridStackWidget;
  isEnabled: boolean;
}

export interface WidgetLayout {
  id: WidgetId;
  grid: GridStackWidget;
  component: Type<any>;
}

export interface WidgetGridData {
  cols: number;
  rows: number;
  x: number;
  y: number;
}

export interface WidgetData {
  codename: `${DashboardWidgetId}`;
  grid: WidgetGridData | null;
  enabled: boolean;
}
